import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState, VFC } from 'react';
import { Helmet } from 'react-helmet-async';

import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import Pagination from 'dashboard/components/Pagination';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunStatus } from 'dashboard/models/workflow-run';
import { WorkflowRunSimulated } from 'dashboard/models/workflow-run-simulated';
import { WorkflowVersion } from 'dashboard/models/workflow-version';
import { WorkflowRunsFilterParams } from 'dashboard/redux/actions/workflow-runs';
import WorkflowRunsConfigurationBar from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsConfigurationBar';
import WorkflowRunsTable, {
  CellType,
  ColumnConfig,
} from 'dashboard/views/DashboardWorkflowRunsView/WorkflowRunsTable';
import Card from 'design-system/components/Card';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { PaginationParams } from 'lib/interfaces/api';
import { captureMessage } from 'lib/reporting';

const getColumnConfigs = (): Record<string, ColumnConfig> => ({
  versionId: {
    type: CellType.Id,
    dataTest: 'case-action-runs-table__workflow-id-header',
    header: 'Version ID',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) =>
      workflowRun.relationships['workflow-version'].data.id,
  },
  runId: {
    type: CellType.Id,
    dataTest: 'case-action-runs-table__workflow-run-id-header',
    header: 'Run ID',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => workflowRun.id,
  },
  dateCreated: {
    type: CellType.Default,
    dataTest: 'case-action-runs-table__created_at-header',
    id: 'case-action-runs-table__created_at-header',
    header: `Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
    sortParam: 'created_at',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => {
      const createdAt = workflowRun.attributes['created-at'];
      return createdAt ? DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT) : '';
    },
  },
  dateCompleted: {
    type: CellType.Default,
    dataTest: 'case-action-runs-table__completed_at-header',
    id: 'case-action-runs-table__completed_at-header',
    header: `Completed at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`,
    sortParam: 'completed',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => {
      const completedAt = workflowRun.attributes['completed-at'];
      return completedAt ? DateTime.fromISO(completedAt).toFormat(DATE_AND_TIME_FORMAT) : '';
    },
  },
  status: {
    type: CellType.Status,
    dataTest: 'case-action-runs-table__status-header',
    header: 'Status',
    getValue: (workflowRun: WorkflowRun | WorkflowRunSimulated) => workflowRun.attributes.status,
  },
});

interface DashboardCaseActionRunsViewProps {
  workflowRuns: WorkflowRun[];
  workflowRunFilters?: WorkflowRunsFilterParams;
  workflowId?: string;
  workflowRunPaginationParams?: PaginationParams;
  workflowVersions: WorkflowVersion[];
  workflowsMap: Record<string, Workflow>;
  pageSize?: number;
  isLoadingWorkflowRuns: boolean;
  isLoadingWorkflowVersions: boolean;
  sortParams?: SortParams;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleDateUpdate: (range: RangeValue<DateValue> | null) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleStatusFilterUpdate: (status: WorkflowRunStatus[]) => void;
  handleVersionFilterUpdate: (versionIds: string[]) => void;
  handleWorkflowIdFilterUpdate: (workflowIds: string[]) => void;
  handleOmniFilterUpdate: (omni: string) => void;
  onLoadMoreVersions?: () => void;
}

const DashboardCaseActionRunsView: VFC<DashboardCaseActionRunsViewProps> = ({
  workflowRuns,
  workflowRunFilters,
  workflowId,
  workflowRunPaginationParams,
  workflowVersions,
  workflowsMap,
  pageSize,
  isLoadingWorkflowRuns,
  isLoadingWorkflowVersions,
  sortParams,
  handlePageLinkClick,
  handlePageSizeClick,
  handleDateUpdate,
  handleSortUpdate,
  handleStatusFilterUpdate,
  handleVersionFilterUpdate,
  handleWorkflowIdFilterUpdate,
  handleOmniFilterUpdate,
  onLoadMoreVersions,
}) => {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const hasUsedFilters = !Object.values(workflowRunFilters).every(isEmpty);

  const allColumnConfigs = useMemo(() => ({ ...getColumnConfigs() }), []);

  const columnConfigOptions = useMemo(
    () =>
      Object.keys(allColumnConfigs).map((key) => ({
        label: allColumnConfigs[key].header,
        value: key,
      })),
    [allColumnConfigs],
  );

  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => setSelectedColumns(columnConfigOptions.map((config) => config.value)),
    [columnConfigOptions],
  );

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const headerTitle = workflowsMap[workflowId]?.attributes?.name
    ? /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      `Case Action Runs: ${workflowsMap[workflowId].attributes.name}`
    : 'Case Action Runs';

  const selectedColumnConfigs = selectedColumns.map((column) => {
    if (!allColumnConfigs[column]) {
      captureMessage(`Column missing from column configs`, 'warning', {
        column,
        columnConfigs: Object.keys(allColumnConfigs).join(','),
        workflowId,
      });
      return {} as ColumnConfig;
    }
    return allColumnConfigs[column];
  });

  return (
    <>
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <Helmet title={`Runs - ${workflowsMap[workflowId]?.attributes?.name} - Actions - Cases`} />
      <InactiveEnvironmentBanner />
      <Container>
        <Header title={headerTitle} />
        <Section data-test='case-action-runs-view'>
          <Card>
            <Card.Body>
              <Card.Title>Workflow Runs</Card.Title>
              <WorkflowRunsConfigurationBar
                workflowId={workflowId ?? null}
                workflowRunFilters={workflowRunFilters}
                workflowVersions={workflowVersions}
                isLoadingWorkflowVersions={isLoadingWorkflowVersions}
                columnConfigOptions={columnConfigOptions}
                columns={selectedColumns}
                handleVersionFilterUpdate={handleVersionFilterUpdate}
                onLoadMoreVersions={onLoadMoreVersions}
                handleStatusFilterUpdate={handleStatusFilterUpdate}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                handleColumnUpdate={setSelectedColumns}
                handleDateUpdate={handleDateUpdate}
                handleOmniFilterUpdate={handleOmniFilterUpdate}
                handleWorkflowIdFilterUpdate={handleWorkflowIdFilterUpdate}
              />
              <WorkflowRunsTable
                workflowRuns={workflowRuns}
                sortParams={sortParams}
                isLoading={isLoadingWorkflowRuns || isLoadingWorkflowVersions}
                hasUsedFilters={hasUsedFilters}
                handleSortUpdate={handleSortUpdate}
                columnConfigs={selectedColumnConfigs}
              />
              {workflowRuns.length > 0 && (
                <Pagination
                  pageSize={pageSize}
                  paginationParams={workflowRunPaginationParams}
                  handlePageSizeClick={handlePageSizeClick}
                  handlePageLinkClick={handlePageLinkClick}
                />
              )}
            </Card.Body>
          </Card>
        </Section>
      </Container>
    </>
  );
};

export default DashboardCaseActionRunsView;
